import { QueryResult } from '@apollo/client';
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { METAMASK_PROMO_COOKIE } from '../components-v2/MetamaskPromo/consts';
import { useUserContext } from '../contexts';
import { ALL_CREDENTIALS } from '../graphql/queries/allCredentials';
import { AllCredentialsQuery, EnumExchangeServiceRequestStatus } from '../graphql/types';
import { getCookie } from '../lib/Auth/getCookie';
import { useQueryAndRefetchOnUserChange } from '../lib/hooks';

type Credential = AllCredentialsQuery['credentials']['edges'][number];
type CredentialsQueryResult = Omit<QueryResult<AllCredentialsQuery>, 'data'> & {
  data: Credential[] | undefined;
  disablePolling: () => void;
  enablePolling: () => void;
  poll: () => void;
};

const POLL_INTERVAL_MS = 3000;
const POLLING_STATUSES = new Set([
  EnumExchangeServiceRequestStatus.changingAccount,
  EnumExchangeServiceRequestStatus.created,
  EnumExchangeServiceRequestStatus.running,
  EnumExchangeServiceRequestStatus.fetchingPrices,
  EnumExchangeServiceRequestStatus.reconciling,
]);

export function credentialsNeedPolling(credentials: Credential[]) {
  return credentials.some(({ status }) => status && POLLING_STATUSES.has(status));
}

const CredentialsContext = React.createContext<CredentialsQueryResult | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export default function CredentialsProvider({ children }: Props) {
  const { isLoggedIn } = useUserContext();
  const [pollForData, setPollForData] = useState(false);
  const pollingEnabledRef = useRef(false);
  const res = useQueryAndRefetchOnUserChange<AllCredentialsQuery>(ALL_CREDENTIALS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: pollForData ? POLL_INTERVAL_MS : 0,
    variables: { extended: true },
    skip: !isLoggedIn,
    onCompleted: ({ credentials: { edges } }) => {
      if (!pollingEnabledRef.current) return;
      setPollForData(credentialsNeedPolling(edges) || Boolean(getCookie(METAMASK_PROMO_COOKIE)));
    },
  });

  const { refetch, stopPolling } = res;
  const enablePolling = useCallback(() => {
    pollingEnabledRef.current = true;
    void refetch();
  }, [refetch]);
  const disablePolling = useCallback(() => {
    pollingEnabledRef.current = false;
    setPollForData(false);
    stopPolling();
  }, [stopPolling]);
  const poll = useCallback(() => {
    if (!pollingEnabledRef.current) return;
    void refetch();
    setPollForData(true);
  }, [refetch]);

  const value = useMemo(
    () => ({
      ...res,
      data: res.data?.credentials.edges.filter(({ source }) => source !== 'externalMovement'),
      disablePolling,
      enablePolling,
      poll,
    }),
    [disablePolling, enablePolling, poll, res],
  );

  return <CredentialsContext.Provider value={value}>{children}</CredentialsContext.Provider>;
}

export function useAllCredentials() {
  const context = React.useContext(CredentialsContext);
  if (!context) {
    throw new Error('useAllCredentials must be used within CredentialsProvider');
  }
  return context;
}
